import React, { Fragment, useCallback, useMemo } from 'react';
import Cell, { EmptyCell } from './Cell';
import { differenceInMinutes } from 'date-fns';
import { getMinutesOfDay } from '@/util/dateTime';

export const BookedSlot = ({
  physician,
  organization,
  onClick,
  grayed,
  physicianSelection,
  startTime,
  endTime,
  colorMode,
  utilization,
  utilizationOutsideSlot,
  actualUtilization,
  actualUtilizationOutsideSlot,
  utilizationOutsideSlotDurationHours,
  actualUtilizationOutsideSlotDurationHours,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
}) => {
  const { startPosition, endPosition } = useMemo(
    () => getBookedSlotPosition(startTime, endTime, dayStartTime, dayEndTime),
    [dayEndTime, dayStartTime, endTime, startTime]
  );

  const clickHandler = useCallback(() => {
    onClick({ from: getMinutesOfDay(startTime), to: getMinutesOfDay(endTime), physician });
  }, [endTime, onClick, physician, startTime]);

  return (
    <Cell
      color={physician?.color}
      physician={physician?.name}
      organization={organization?.name}
      category={physician?.category}
      speciality={physician?.speciality}
      onClick={clickHandler}
      grayed={grayed}
      highlighted={physicianSelection === physician?.id}
      start={startPosition}
      end={endPosition}
      colorMode={colorMode}
      utilization={utilization}
      utilizationOutsideSlot={utilizationOutsideSlot}
      actualUtilization={actualUtilization}
      actualUtilizationOutsideSlot={actualUtilizationOutsideSlot}
      utilizationOutsideSlotDurationHours={utilizationOutsideSlotDurationHours}
      actualUtilizationOutsideSlotDurationHours={actualUtilizationOutsideSlotDurationHours}
    />
  );
};

const BookedSlotWrapper = ({
  booking,
  onClick,
  grayed,
  physicianSelection,
  colorMode,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
}) => {
  const onClickWithBooking = useCallback(
    val => {
      onClick({ ...val, booking });
    },
    [onClick, booking]
  );
  return (
    <BookedSlot
      physician={booking.physician}
      organization={booking.organization}
      onClick={onClickWithBooking}
      grayed={grayed}
      physicianSelection={physicianSelection}
      startTime={booking.start}
      endTime={booking.end}
      colorMode={colorMode}
      utilization={booking.utilization.percentage}
      utilizationOutsideSlot={booking.utilization.hasOutsideSlot}
      utilizationOutsideSlotDurationHours={booking.utilization.outsideSlotDuration}
      actualUtilization={booking.actualUtilization.percentage}
      actualUtilizationOutsideSlot={booking.actualUtilization.hasOutsideSlot}
      actualUtilizationOutsideSlotDurationHours={booking.actualUtilization.outsideSlotDuration}
      dayStartTime={dayStartTime}
      dayEndTime={dayEndTime}
    />
  );
};

const transparentStyle = { backgroundColor: 'transparent' };

const EmptySlot = ({
  startTime,
  endTime,
  onClick,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
  isFullDay = false,
}) => {
  const { startPosition, endPosition, actualStartMinutes, actualEndMinutes } = useMemo(
    () => getEmptySlotPosition(startTime, endTime, dayStartTime, dayEndTime, isFullDay),
    [dayEndTime, dayStartTime, endTime, isFullDay, startTime]
  );

  const clickHandler = useCallback(() => {
    const fromMinutes = isFullDay ? 7 * 60 : actualStartMinutes;
    const toMinutes = isFullDay ? 15 * 60 : actualEndMinutes;
    onClick({ from: fromMinutes, to: toMinutes });
  }, [actualEndMinutes, actualStartMinutes, isFullDay, onClick]);

  return <EmptyCell onClick={clickHandler} start={startPosition} end={endPosition} style={transparentStyle} />;
};

const Slot = ({
  bookings = [],
  onClick,
  grayed,
  physicianSelection,
  date,
  colorMode,
  dayStartTime = '05:00',
  dayEndTime = '17:00',
}) => {
  const sorted = useMemo(() => {
    const safeStartTime = dayStartTime || '05:00';
    const safeEndTime = dayEndTime || '17:00';
    const [startHour, startMinute] = safeStartTime.split(':').map(Number);
    const [endHour, endMinute] = safeEndTime.split(':').map(Number);
    const dayStartMinutes = startHour * 60 + startMinute;
    const dayEndMinutes = endHour * 60 + endMinute;

    return bookings
      .filter(booking => {
        const bookingStartMinutes = getMinutesOfDay(booking.start);
        const bookingEndMinutes = getMinutesOfDay(booking.end);

        return bookingEndMinutes > dayStartMinutes && bookingStartMinutes < dayEndMinutes;
      })
      .sort((a, b) => getMinutesOfDay(a.start) - getMinutesOfDay(b.start));
  }, [bookings, dayStartTime, dayEndTime]);

  const availableSlots = useMemo(
    () => getAvailableSlots(sorted, date, dayStartTime, dayEndTime),
    [sorted, date, dayStartTime, dayEndTime]
  );

  return (
    <Fragment>
      {availableSlots.map((slot, i) => (
        <EmptySlot
          key={`empty-${slot.startTime.getTime?.() ?? 0}-${slot.endTime.getTime?.() ?? i}`}
          {...slot}
          onClick={onClick}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
        />
      ))}
      {sorted.map(booking => (
        <BookedSlotWrapper
          key={booking.id}
          booking={booking}
          onClick={onClick}
          grayed={grayed}
          physicianSelection={physicianSelection}
          colorMode={colorMode}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
        />
      ))}
    </Fragment>
  );
};

function getAvailableSlots(booked, date, dayStartTime = '05:00', dayEndTime = '17:00') {
  const availableSlots = [];

  const safeStartTime = dayStartTime || '05:00';
  const safeEndTime = dayEndTime || '17:00';
  const [startHour, startMinute] = safeStartTime.split(':').map(Number);
  const [endHour, endMinute] = safeEndTime.split(':').map(Number);

  const dayStart = new Date(date);
  dayStart.setHours(startHour, startMinute, 0, 0);

  const dayEnd = new Date(date);
  dayEnd.setHours(endHour, endMinute, 0, 0);

  const bookings = [{ end: dayStart }, ...booked];
  for (let i = 0; i < bookings.length; i++) {
    const start = bookings[i].end;
    const end = bookings[i + 1]?.start || dayEnd;

    if (differenceInMinutes(end, start) > 1) {
      const isFullDay = booked.length === 0 && i === 0;
      availableSlots.push({ startTime: start, endTime: end, isFullDay });
    }
  }
  return availableSlots;
}

function getDayStartMinutes(dayStartTime) {
  const safeStartTime = dayStartTime || '05:00';
  const [hours, minutes] = safeStartTime.split(':').map(Number);
  return hours * 60 + minutes;
}

function getDayEndMinutes(dayEndTime) {
  const safeEndTime = dayEndTime || '17:00';
  const [hours, minutes] = safeEndTime.split(':').map(Number);
  return hours * 60 + minutes;
}

function getBookedSlotPosition(startTime, endTime, dayStartTime, dayEndTime) {
  const dayStartMinutes = getDayStartMinutes(dayStartTime);
  const dayEndMinutes = getDayEndMinutes(dayEndTime);

  const totalDayMinutes = dayEndMinutes - dayStartMinutes;

  const rawStartPosition = ((getMinutesOfDay(startTime) - dayStartMinutes) / totalDayMinutes) * 100;
  const rawEndPosition = ((getMinutesOfDay(endTime) - dayStartMinutes) / totalDayMinutes) * 100;

  const startPosition = Math.max(0, Math.min(100, rawStartPosition));
  const endPosition = Math.max(0, Math.min(100, rawEndPosition));

  return {
    startPosition,
    endPosition,
  };
}

function getEmptySlotPosition(startTime, endTime, dayStartTime, dayEndTime, isFullDay) {
  const dayStartMinutes = getDayStartMinutes(dayStartTime);
  const dayEndMinutes = getDayEndMinutes(dayEndTime);

  const totalDayMinutes = dayEndMinutes - dayStartMinutes;

  const actualStartMinutes = getMinutesOfDay(startTime);
  const actualEndMinutes = getMinutesOfDay(endTime);

  const displayStartMinutes = isFullDay ? 7 * 60 : actualStartMinutes;
  const displayEndMinutes = isFullDay ? 15 * 60 : actualEndMinutes;

  const rawStartPosition = ((displayStartMinutes - dayStartMinutes) / totalDayMinutes) * 100;
  const rawEndPosition = ((displayEndMinutes - dayStartMinutes) / totalDayMinutes) * 100;

  const startPosition = Math.max(0, Math.min(100, rawStartPosition));
  const endPosition = Math.max(0, Math.min(100, rawEndPosition));

  return {
    startPosition,
    endPosition,
    actualStartMinutes,
    actualEndMinutes,
  };
}

export default Slot;
